/**
 * Frontend Tenant Configuration System
 * Manages multi-tenant branding and configuration for Bloom SaaS
 */

import { getRuntimeHyperswitchConfig } from './tenant-runtime';

export interface TenantBranding {
  tenantId: string;
  title: string;
  logo?: string;
  favicon?: string;
  companyName: string;
  primaryColor: string;
}

export interface TenantConfig {
  tenantId: string;
  branding: TenantBranding;
  features: {
    enabled: string[];
    tier: 'basic' | 'standard' | 'premium';
  };
  payments: {
    hyperswitch: {
      publishableKey: string;
      environment: 'sandbox' | 'staging' | 'production';
    };
  };
}

/**
 * Get tenant configuration from environment variables
 */
export const getTenantConfig = (): TenantConfig => {
  const tenantId = import.meta.env.VITE_TENANT_ID || 'bloom';
  
  return {
    tenantId,
    branding: {
      tenantId,
      title: import.meta.env.VITE_BRANDING_TITLE || 'Bloom Health Platform',
      logo: import.meta.env.VITE_BRANDING_LOGO || '',
      favicon: import.meta.env.VITE_BRANDING_FAVICON || '/favicon.svg',
      companyName: import.meta.env.VITE_BRANDING_COMPANY_NAME || '5 Axis Health',
      primaryColor: import.meta.env.VITE_BRANDING_PRIMARY_COLOR || '#3B82F6'
    },
    features: {
      enabled: (import.meta.env.VITE_FEATURES_ENABLED || 'analytics,bulk-orders').split(',').map((f: string) => f.trim()),
      tier: (import.meta.env.VITE_FEATURES_TIER as 'basic' | 'standard' | 'premium') || 'standard'
    },
    payments: {
      hyperswitch: {
        publishableKey: import.meta.env.VITE_HYPERSWITCH_PUBLISHABLE_KEY || '',
        // Auto-detect environment from publishable key prefix, or use env var
        // pk_snd_ = sandbox, pk_prd_ = production
        environment: (import.meta.env.VITE_HYPERSWITCH_ENVIRONMENT as 'sandbox' | 'staging' | 'production') || 
          ((import.meta.env.VITE_HYPERSWITCH_PUBLISHABLE_KEY || '').startsWith('pk_prd_') ? 'production' : 'sandbox')
      }
    }
  };
};

/**
 * Validate hex color format
 */
export const isValidHexColor = (color: string): boolean => {
  return /^#[0-9A-Fa-f]{6}$/.test(color);
};

/**
 * Generate lighter/darker shades of the primary color
 */
export const generateColorShades = (primaryColor: string) => {
  if (!isValidHexColor(primaryColor)) {
    primaryColor = '#3B82F6'; // Default blue
  }

  // Convert hex to RGB
  const hex = primaryColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);

  // Generate lighter shade (add 30 to each component, max 255)
  const lighter = `#${Math.min(255, r + 30).toString(16).padStart(2, '0')}${Math.min(255, g + 30).toString(16).padStart(2, '0')}${Math.min(255, b + 30).toString(16).padStart(2, '0')}`;
  
  // Generate darker shade (subtract 30 from each component, min 0)
  const darker = `#${Math.max(0, r - 30).toString(16).padStart(2, '0')}${Math.max(0, g - 30).toString(16).padStart(2, '0')}${Math.max(0, b - 30).toString(16).padStart(2, '0')}`;

  return {
    primary: primaryColor,
    lighter,
    darker
  };
};

/**
 * Check if a feature is enabled for the current tenant
 */
export const isFeatureEnabled = (feature: string): boolean => {
  const config = getTenantConfig();
  return config.features.enabled.includes(feature);
};

/**
 * Get tenant tier
 */
export const getTenantTier = (): 'basic' | 'standard' | 'premium' => {
  const config = getTenantConfig();
  return config.features.tier;
};

/**
 * Get Hyperswitch payment configuration for the current tenant
 */
export const getHyperswitchConfig = () => {
  const config = getCurrentTenantConfig();
  return config.payments.hyperswitch;
};

// Global tenant configuration instance
let tenantConfig: TenantConfig | null = null;

/**
 * Initialize tenant configuration (should be called once at app startup)
 */
export const initializeTenantConfig = (): TenantConfig => {
  if (tenantConfig) {
    return tenantConfig;
  }

  tenantConfig = getTenantConfig();
  
  console.log(`🏥 Frontend tenant initialized: ${tenantConfig.branding.companyName} (${tenantConfig.tenantId})`);
  
  return tenantConfig;
};

/**
 * Get current tenant configuration (must be initialized first).
 *
 * The Hyperswitch publishable key is NOT reliable at build time: one shared
 * frontend image serves every tenant, so the baked VITE_HYPERSWITCH_PUBLISHABLE_KEY
 * is empty. We overlay the correct per-tenant key fetched at runtime from
 * /api/tenant/config (populated before any route renders — App gates on it).
 * Falls back to the build-time value when the runtime config isn't loaded.
 */
export const getCurrentTenantConfig = (): TenantConfig => {
  const base = tenantConfig ?? initializeTenantConfig();

  const runtimeHyperswitch = getRuntimeHyperswitchConfig();
  if (runtimeHyperswitch?.publishableKey) {
    return {
      ...base,
      payments: {
        hyperswitch: {
          publishableKey: runtimeHyperswitch.publishableKey,
          environment: runtimeHyperswitch.environment,
        },
      },
    };
  }

  return base;
};
